import { CLASSIFICATION_TASKS } from './aiModelConstants';
import {
  AiModelClassificationLabel,
  AiModelDataType,
  AiModelFormState,
  AiModelInputField,
  AiModelInvokeContract,
  AiModelOutputField,
  AiRegisteredModel,
} from './aiModelTypes';

function jsonType(dataType: AiModelDataType): string {
  switch (dataType) {
    case 'integer':
      return 'integer';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return 'array';
    case 'object':
      return 'object';
    default:
      return 'string';
  }
}

function parseExample(value: string, dataType: AiModelDataType): unknown {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (dataType === 'number' || dataType === 'integer') {
    const num = Number(trimmed);
    return Number.isNaN(num) ? trimmed : num;
  }
  if (dataType === 'boolean') {
    if (trimmed.toLowerCase() === 'true') return true;
    if (trimmed.toLowerCase() === 'false') return false;
  }
  if (dataType === 'array' || dataType === 'object') {
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export function buildInputJsonSchema(fields: AiModelInputField[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const field of fields) {
    const name = field.name.trim();
    if (!name) continue;
    properties[name] = {
      type: jsonType(field.dataType),
      nullable: field.nullable,
      example: parseExample(field.example, field.dataType),
    };
    if (field.required) required.push(name);
  }
  return {
    type: 'object',
    properties,
    required,
    additionalProperties: false,
  };
}

export function buildOutputJsonSchema(
  fields: AiModelOutputField[],
  labels: AiModelClassificationLabel[]
): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  const labelMap = Object.fromEntries(
    labels.filter((row) => row.value.trim()).map((row) => [row.value.trim(), row.label.trim()])
  );
  for (const field of fields) {
    const name = field.name.trim();
    if (!name) continue;
    properties[name] = {
      type: jsonType(field.dataType),
      scoreSupported: field.scoreSupported,
      ...(field.dataType === 'category' && Object.keys(labelMap).length ? { labels: labelMap } : {}),
    };
    required.push(name);
  }
  return {
    type: 'object',
    properties,
    required,
    additionalProperties: false,
  };
}

export function buildSampleRequestObject(fields: AiModelInputField[]): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const field of fields) {
    const name = field.name.trim();
    if (!name) continue;
    const parsed = parseExample(field.example, field.dataType);
    body[name] = parsed === undefined ? null : parsed;
  }
  return body;
}

export function buildSampleResponseObject(
  fields: AiModelOutputField[],
  labels: AiModelClassificationLabel[]
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const firstLabel = labels.find((row) => row.label.trim())?.label.trim();
  for (const field of fields) {
    const name = field.name.trim();
    if (!name) continue;
    if (field.dataType === 'category') {
      body[name] = firstLabel || 'Fraud';
      continue;
    }
    if (field.dataType === 'number' || field.dataType === 'integer') {
      body[name] = field.scoreSupported ? 0.94 : 1;
      continue;
    }
    if (field.dataType === 'boolean') {
      body[name] = true;
      continue;
    }
    body[name] = field.name;
  }
  return body;
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function parseJsonOrNull(text: string): unknown | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function buildInvokeContract(form: AiModelFormState | AiRegisteredModel): AiModelInvokeContract {
  return {
    code: form.code,
    version: form.version,
    environment: form.environment,
    provider: form.provider,
    endpointId: form.endpointId,
    location: form.location,
    authLabel: form.authLabel,
    httpMethod: form.httpMethod,
    contentType: form.contentType,
    payloadFormat: form.payloadFormat,
    timeoutMs: form.timeoutMs,
    retryCount: form.retryCount,
    task: form.task,
    modality: form.modality,
    inferenceMode: form.inferenceMode,
    preprocessingIncluded: form.preprocessingIncluded,
    inputSchema: buildInputJsonSchema(form.inputFields),
    outputSchema: buildOutputJsonSchema(form.outputFields, form.classificationLabels),
    classificationLabels: Object.fromEntries(
      form.classificationLabels
        .filter((row) => row.value.trim())
        .map((row) => [row.value.trim(), row.label.trim()])
    ),
    sampleRequest: parseJsonOrNull(form.sampleRequest) ?? form.sampleRequest,
    sampleResponse: parseJsonOrNull(form.sampleResponse) ?? form.sampleResponse,
  };
}

export function isClassificationTask(task: AiModelFormState['task']): boolean {
  return CLASSIFICATION_TASKS.includes(task);
}
